import { Outlet, type RouteObject } from "react-router-dom";
import PublicRoute from "src/components/auth-routes/PublicRoute";
import ProtectedLayout from "src/layouts/ProtectedLayout";
import Login from "src/pages/Login/Login";
import Signup from "src/pages/Signup/Signup";
import RegistrationLayout from "src/layouts/RegistrationLayout";
import DashboardLayout from "src/layouts/DashboardLayout";
import PatientAccount from "src/pages/PatientDashboard/PatientAccount";
import PatientAccountNavBar from "src/components/NavBars/PatientAccountNavBar";
import TransactionHistory from "src/pages/PatientDashboard/TransactionHistory";
import HelpCenter from "src/pages/PatientDashboard/HelpCenter";
import PaymentMethods from "src/pages/PatientDashboard/PaymentMethods";
import Messaging from "src/pages/PatientDashboard/Messaging";
import MessagingThread from "src/pages/PatientDashboard/Messaging/MessagingThread";
import MessagingProvider from "src/context/MessagingProvider";
import MessageThreadsProvider from "src/context/MessageThreadsProvider";
import MagicLinkCallback from "src/pages/MagicLinkCallback";
import AuthQueue from "src/pages/transitions/AuthQueue";
import Welcome from "src/pages/Welcome";
import SocialLoginTransition from "src/pages/transitions/SocialLoginTransition";
import { Paths } from "../paths";
import { Matches } from "../matches";
import { checkInRoutes } from "./checkInRoutes";
import { registrationRoutes } from "./registrationRoutes";
import { refillRoutes } from "./refillRoutes";
import PatientDashboard from "src/pages/PatientDashboard";
import Care from "src/pages/PatientDashboard/Care";
import Subscriptions from "src/pages/PatientDashboard/PatientAccount/Subscriptions";
import SubscriptionDetails from "src/pages/PatientDashboard/PatientAccount/Subscriptions/SubscriptionDetails";
import ProductLayout from "src/layouts/ProductLayout";
import PreferredLanguageLayout from "src/layouts/PreferredLanguageLayout";
import PopupProvider from "src/context/PopupProvider";
import ResetPassword from "src/pages/ResetPassword";
import UpdatePassword from "src/pages/UpdatePassword";
import Payment from "src/pages/Onboarding/Payment";
import PricesProvider from "src/context/PricesProvider";
import Welcome2 from "src/pages/Welcome2/Welcome2";
import { upsellRoutes } from "./upsellRoutes";
import Shop from "src/pages/Shop";
import DashboardPaymentLayout from "src/layouts/DashboardPaymentLayout";
import LatestOrderProvider from "src/context/LatestOrder/LatestOrderProvider";
import ManageSubscription from "src/pages/PatientDashboard/PatientAccount/Subscriptions/ManageSubscription";
import CancelSubscription from "src/pages/PatientDashboard/PatientAccount/Subscriptions/CancelSubscription";
import { cancellationRoutes } from "./cancellationRoutes";
import LatestPrescriptionProvider from "src/context/LatestPrescriptionProvider";
import NotFound from "src/pages/NotFound";
import PageTrackerProvider from "src/context/PageTrackerProvider";
import ProtectedRoute from "src/components/auth-routes/ProtectedRoute";
import LogOut from "src/pages/Logout";
import Glp1s from "src/pages/Glp1s";
import RootLayout from "src/layouts/RootLayout";
import { store } from "src/app/store";
import { productsApi } from "src/api/services/productsApi";
import { visitApi } from "src/api/services/visitApi";
import ProductVisitLayout from "src/layouts/ProductVisitLayout";
import { orderApi } from "src/api/services/ordersApi";
import ReferAFriend from "src/pages/ReferAFriend";
import { referralsApi } from "src/api/services/referralsApi";
import RecommendedDosageProvider from "src/context/RecommendedDosageProvider";
import { patientSubscriptionApi } from "src/api/services/patientSubscriptionApi";
import { getMeLoader } from "src/loaders/getMeLoader";
import { getOrdersLoaders } from "src/loaders/getOrdersLoader";
import InsuranceDetails from "src/pages/InsuranceDetails";
import PreferredPharmacyInformation from "src/pages/PreferredPharmacyInformation";

export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    children: [
      {
        path: "*",
        element: <NotFound />,
      },
      {
        path: "/quality-review",
        element: <MagicLinkCallback />,
      },
      {
        path: "transition",
        element: <SocialLoginTransition />,
      },
      {
        path: `/${Matches.LANGUAGE}`,
        element: <PreferredLanguageLayout />,
        children: [
          {
            path: `${Matches.PRODUCT_NAME}/${Matches.VARIANT}`,
            element: <ProductLayout />,
            loader: async ({ params }) => {
              if (params.product != null) {
                return store
                  .dispatch(
                    productsApi.endpoints.getProductBySlug.initiate(
                      params.product,
                    ),
                  )
                  .unwrap();
              }
            },
            children: [
              {
                path: "signup",
                element: <Signup />,
              },
              {
                element: <RegistrationLayout showLanguageSelect={false} />,
                children: [
                  {
                    path: "welcome",
                    element: <Welcome />,
                  },
                  {
                    path: "welcome-2",
                    element: <Welcome2 />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        element: <ProtectedLayout />,
        loader: getMeLoader,
        children: [
          {
            path: `/${Matches.PRODUCT_NAME}/${Matches.VISIT_ID}`,
            element: <ProductVisitLayout />,
            loader: ({ params }) => {
              if (params.product != null && params.visitId != null) {
                return Promise.all([
                  store
                    .dispatch(
                      productsApi.endpoints.getProductBySlug.initiate(
                        params.product,
                      ),
                    )
                    .unwrap(),
                  store
                    .dispatch(
                      visitApi.endpoints.getVisit.initiate(params.visitId),
                    )
                    .unwrap(),
                ]);
              }
            },
            children: [
              ...registrationRoutes,
              ...checkInRoutes,
              ...refillRoutes,
              ...upsellRoutes,
              ...cancellationRoutes,
            ],
          },
          { path: "*", element: <NotFound /> },
        ],
      },
      {
        element: <ProtectedLayout />,
        children: [
          {
            element: (
              <PageTrackerProvider>
                <MessageThreadsProvider>
                  <LatestPrescriptionProvider>
                    <LatestOrderProvider>
                      <DashboardLayout />
                    </LatestOrderProvider>
                  </LatestPrescriptionProvider>
                </MessageThreadsProvider>
              </PageTrackerProvider>
            ),
            children: [
              {
                path: Paths.REFERRALS,
                element: <ReferAFriend />,
                loader: async () => {
                  await store
                    .dispatch(referralsApi.endpoints.myReferralCode.initiate())
                    .unwrap();
                },
              },
              {
                path: Paths.MESSAGING,
                element: (
                  <MessagingProvider>
                    <Outlet />
                  </MessagingProvider>
                ),
                children: [
                  { element: <Messaging active={true} />, index: true },
                  { path: Matches.THREAD_ID, element: <MessagingThread /> },
                ],
              },
              {
                path: Paths.CARE,
                loader: getOrdersLoaders,
                element: (
                  <RecommendedDosageProvider>
                    <Care />
                  </RecommendedDosageProvider>
                ),
                children: [{ path: Matches.PRODUCT_ID }],
              },
              {
                path: Paths.PATIENT_DASHBOARD,
                element: (
                  <PopupProvider>
                    <RecommendedDosageProvider>
                      <PatientDashboard />
                    </RecommendedDosageProvider>
                  </PopupProvider>
                ),
              },
              {
                element: <PatientAccountNavBar />,
                path: Paths.MY_ACCOUNT,
                children: [
                  {
                    path: "messages",
                    children: [
                      {
                        element: <Messaging active={false} />,
                        index: true,
                      },
                      {
                        path: Matches.THREAD_ID,
                        element: (
                          <MessagingProvider>
                            <MessagingThread />
                          </MessagingProvider>
                        ),
                      },
                    ],
                  },
                  {
                    path: Paths.MY_ACCOUNT_OVERVIEW,
                    element: <PatientAccount />,
                  },
                  {
                    path: Paths.MY_ACCOUNT_TRANSACTION_HISTORY,
                    element: <TransactionHistory />,
                  },
                  {
                    path: Paths.MY_ACCOUNT_HELP_CENTER,
                    element: <HelpCenter />,
                  },
                  {
                    path: Paths.MY_PAYMENT_METHODS,
                    element: <PaymentMethods />,
                  },
                  {
                    path: Paths.MY_SUBSCRIPTIONS,
                    loader: async () => {
                      await store
                        .dispatch(
                          patientSubscriptionApi.endpoints.getSubscriptions.initiate(),
                        )
                        .unwrap();
                    },
                    children: [
                      { index: true, element: <Subscriptions /> },
                      {
                        path: Matches.SUBSCRIPTION_ID,
                        loader: async ({ params }) => {
                          if (params.subscriptionId != null) {
                            return store
                              .dispatch(
                                patientSubscriptionApi.endpoints.getSubscriptionById.initiate(
                                  Number(params.subscriptionId),
                                ),
                              )
                              .unwrap();
                          }
                        },
                        children: [
                          {
                            index: true,
                            element: <SubscriptionDetails />,
                          },
                          {
                            path: "manage",
                            element: <ManageSubscription />,
                          },
                          {
                            path: "cancel",
                            element: <CancelSubscription />,
                          },
                        ],
                      },
                      { path: "*", element: <NotFound /> },
                    ],
                  },
                  {
                    path: Paths.MY_ACCOUNT_INSURANCE_DETAILS,
                    element: <InsuranceDetails />,
                  },
                  {
                    path: Paths.MY_ACCOUNT_PREFERRED_PHARMACY,
                    element: <PreferredPharmacyInformation />,
                  },
                ],
              },
              {
                element: <Shop />,
                path: Paths.SHOP,
              },
              {
                element: <Glp1s />,
                path: Paths.GLP1,
                loader: async () => {
                  const latestOrder = await store
                    .dispatch(
                      orderApi.endpoints.getLatestOTWeightLossOrder.initiate(),
                    )
                    .unwrap();
                  if (latestOrder != null) {
                    store
                      .dispatch(
                        productsApi.endpoints.getOneTimeProductsBySlug.initiate(
                          {
                            slug: latestOrder.product.slug,
                            number_of_months: 1,
                          },
                        ),
                      )
                      .unwrap();
                    store
                      .dispatch(
                        productsApi.endpoints.getOneTimeProductsBySlug.initiate(
                          {
                            slug: latestOrder.product.slug,
                            number_of_months: 3,
                          },
                        ),
                      )
                      .unwrap();
                  }
                },
              },
              { path: "*", element: <NotFound /> },
            ],
          },
          {
            path: `/${Paths.ADD_ONS}/${Matches.PRODUCT_NAME}`,
            element: <DashboardPaymentLayout />,
            children: [
              {
                element: <ProductLayout />,
                children: [
                  {
                    element: (
                      <PricesProvider>
                        <Payment />
                      </PricesProvider>
                    ),
                    index: true,
                  },
                  { path: "*", element: <NotFound /> },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "/login",
        element: (
          <PublicRoute>
            <Login />
          </PublicRoute>
        ),
      },
      {
        path: "/logout",
        element: (
          <ProtectedRoute>
            <LogOut />
          </ProtectedRoute>
        ),
      },
      {
        path: "/reset-password",
        element: <ResetPassword />,
      },
      {
        element: <ProtectedLayout />,
        children: [{ path: "/update-password", element: <UpdatePassword /> }],
      },
      {
        element: <ProtectedLayout />,
        children: [
          {
            path: "/auth-queue",
            element: <AuthQueue />,
          },

          {
            path: "/",
            element: <AuthQueue />,
          },
        ],
      },
    ],
  },
];
